import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Eye, EyeOff, Lock, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Signin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "/portal";
  const { setSession, isAuthed } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    if (isAuthed) navigate(next, { replace: true });
  }, [isAuthed, next, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      errs.email = "Enter a valid email.";
    if (!password) errs.password = "Password is required.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setAuthError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: email.trim().toLowerCase(),
        password,
      });
      setSession(data.token, data.customer);
      toast.success("Welcome back!", {
        description: `Signed in as ${data.customer.full_name}.`,
      });
      navigate(next, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setAuthError("Email or password is incorrect.");
      } else if (status === 429) {
        setAuthError("Too many attempts. Try again in 15 minutes.");
      } else {
        setAuthError(formatApiError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="bg-gradient-to-b from-brand-50 to-white min-h-[calc(100vh-5rem)] flex items-center"
      data-testid="page-signin"
    >
      <div className="container-page py-7 sm:py-10 lg:py-20">
        <div className="max-w-sm lg:max-w-md mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-brand-700 transition-colors mb-4 lg:mb-6"
            data-testid="signin-back-home"
          >
            <ArrowLeft size={12} /> Back home
          </Link>
          <p className="overline mb-2 lg:mb-3">Customer Portal</p>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-extrabold tracking-tight text-ink">
            Welcome <span className="text-brand">back.</span>
          </h1>
          <p className="mt-2 lg:mt-4 text-sm lg:text-base text-slate-600">
            Sign in to manage your plan, schedule, and photos.
          </p>

          <form
            onSubmit={submit}
            noValidate
            className="mt-5 lg:mt-8 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 lg:p-8 shadow-soft"
            data-testid="signin-form"
          >
            <label
              htmlFor="signin-email"
              className="block text-[10px] lg:text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-1.5 lg:mb-2"
            >
              Email
            </label>
            <div className="relative mb-4 lg:mb-5">
              <Mail
                size={14}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                id="signin-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="h-11 lg:h-12 w-full rounded-xl border-2 border-slate-200 bg-white pl-10 pr-4 text-base text-ink placeholder:text-slate-400 focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/15"
                data-testid="signin-input-email"
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-500">{errors.email}</p>
              )}
            </div>

            <label
              htmlFor="signin-password"
              className="block text-[10px] lg:text-[11px] font-bold uppercase tracking-[0.18em] text-slate-700 mb-1.5 lg:mb-2"
            >
              Password
            </label>
            <div className="relative">
              <Lock
                size={14}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                id="signin-password"
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                className="h-11 lg:h-12 w-full rounded-xl border-2 border-slate-200 bg-white pl-10 pr-12 text-base text-ink placeholder:text-slate-400 focus:outline-none focus:border-brand focus:ring-4 focus:ring-brand/15"
                data-testid="signin-input-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-500 hover:text-brand-700 hover:bg-slate-100 transition-colors"
                data-testid="signin-toggle-password"
              >
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1.5 text-xs text-red-500">{errors.password}</p>
            )}

            <p className="mt-2 lg:mt-3 text-[11px] lg:text-xs text-slate-500">
              Forgot password?{" "}
              <a
                href="mailto:silkysmoothpowerwash@gmail.com"
                className="text-brand-700 font-semibold hover:underline"
              >
                Email us to reset.
              </a>
            </p>

            {authError && (
              <div
                role="alert"
                className="mt-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
                data-testid="signin-error"
              >
                {authError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full mt-5 lg:mt-6 h-11 lg:h-12 disabled:opacity-60"
              data-testid="signin-submit"
            >
              {submitting ? "Signing in…" : "Sign In"}
              {!submitting && <ArrowRight size={14} />}
            </button>

            <p className="mt-4 lg:mt-5 text-center text-xs lg:text-sm text-slate-600">
              New here?{" "}
              <Link
                to="/signup"
                className="font-semibold text-brand-700 hover:text-brand"
                data-testid="signin-signup-link"
              >
                Create an account →
              </Link>
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
